/* eslint-disable react/no-danger */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { parse as parseQuery, stringify as stringifyQuery } from 'query-string';
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation, useSearch } from 'wouter';

import useItems from '../../hooks/useItems';
import * as AppPropTypes from '../../lib/PropTypes';

import { useApi } from '../../contexts/ApiContext';
import { useSite } from '../../contexts/SiteContext';
import { useFloatingVideo } from '../../contexts/VideoContext';
import Blocks from '../blocks/Blocks';
import DrawnArrow from '../buttons/DrawnArrow';
import RoundedButton from '../buttons/Rounded';
import GridWithFilters from '../lists/GridWithFilters';
import Detector from '../partials/Detector';
import SectionPageBackground from '../partials/SectionPageBackground';
import UsersVisualisation from '../partials/UsersVisualisation';

import styles from '../../../styles/pages/section.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    className: PropTypes.string,
};

const defaultProps = {
    className: null,
};

const SectionPage = ({ page: sectionPage, className }) => {
    const api = useApi();
    const [pathname, navigate] = useLocation();
    const search = useSearch();
    const { handle: siteHandle = null } = useSite();

    const { setFloatingVideo } = useFloatingVideo();
    // Pick the first video to fill the slot
    const videoEmbed = useMemo(() => {
        const { blocks } = sectionPage || {};
        const stacks = (blocks || []).filter((blk) => blk.type === 'stack');
        const finalBlocks = blocks.concat(
            stacks.reduce((acc, stack) => acc.concat(stack.blocks || []), []),
        );
        const block = (finalBlocks || []).find(
            (blk) => blk.type === 'embed' || blk.type === 'video',
        );
        const { embed, video } = block || {};
        return embed || video || null;
    }, [sectionPage]);

    useEffect(() => {
        let id = null;
        if (videoEmbed !== null) {
            id = setTimeout(() => {
                setFloatingVideo(videoEmbed);
            }, 300);
        }
        return () => {
            clearTimeout(id);
        };
    }, [videoEmbed, setFloatingVideo]);

    const { id, filters = [], slug, handle, blocks = null } = sectionPage || {};

    const [paginationQuery, setPaginationQuery] = useState({ page: 1, count: 12 });
    const searchQuery = useMemo(
        () => ({
            ...parseQuery(search || null, {
                arrayFormat: 'bracket',
            }),
        }),
        [search, id],
    );
    const [query, setQuery] = useState({ ...paginationQuery, ...searchQuery });

    const onQueryChange = useCallback(
        (newQuery) => {
            const { page = 1, count = 12, ...others } = newQuery || {};
            setPaginationQuery({ page, count });
            setQuery({ ...(others || {}), page, count });
        },
        [setPaginationQuery, setQuery],
    );

    const getPage = useCallback(
        (p, c, q) => api.pages.getProjects(slug, p, c, q),
        [slug, api, query],
    );

    const store = useMemo(() => `projects_${slug}`, [slug]);

    const {
        allItems: items,
        allLoaded,
        loadNextPage,
        loading,
        pagination,
    } = useItems({
        store,
        getPage,
        // pages: articles,
        paginated: true,
        query,
        onQueryChange,
    });

    const onFiltersChange = useCallback(
        (values) => {
            const { page, count, ...others } = values || {};
            onQueryChange(others);
        },
        [onQueryChange, navigate, pathname],
    );

    useEffect(() => {
        const { page, count, ...others } = query || {};
        const hasParams = Object.keys(others || {}).length > 0;
        navigate(
            hasParams
                ? `${pathname}?${stringifyQuery({ ...(others || null) }, { arrayFormat: 'bracket' })}`
                : `${pathname}`,
        );
    }, [query, navigate, pathname]);

    useEffect(() => {
        if (searchQuery !== null && Object.keys(searchQuery).length === 0) {
            if (siteHandle !== 'quebec') {
                onQueryChange({ market: ['france'] });
            } else {
                onQueryChange({});
            }
        }
    }, [siteHandle, pathname]);

    const { page = 1, lastPage = null } = pagination || {};

    const scrolltoTop = useCallback(() => {
        window.scrollTo({ top: 0 });
    }, []);

    const loadDisabled =
        (items || []).length === 0 ||
        loading ||
        page >= lastPage ||
        lastPage === null ||
        lastPage === 1;

    const endOfPage =
        (items || []).length === 0 && allLoaded && !loading ? (
            <div className={styles.next}>
                <p>
                    <FormattedMessage
                        defaultMessage="No results found for these filters, try something else!"
                        description="Error label"
                    />
                </p>
            </div>
        ) : (
            <div className={styles.next}>
                <DrawnArrow drawn className={styles.arrow} onClick={scrolltoTop} />
            </div>
        );

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
        >
            <SectionPageBackground className={styles.header} pageHandle={handle} />
            <div className={styles.content}>
                {handle === 'technologies' ? <UsersVisualisation /> : null}
                {blocks !== null ? (
                    <Blocks
                        className={styles.blocks}
                        blocks={blocks.map((block) => {
                            const { type } = block;
                            return type === 'gallery'
                                ? {
                                      ...block,
                                      imageClassName: styles.gallery,
                                  }
                                : block;
                        })}
                        isContainer
                        firstInPage
                        typeClassName={{
                            title: styles.title,
                        }}
                    />
                ) : null}
                <GridWithFilters
                    items={items || []}
                    filters={filters}
                    query={query}
                    onQueryChange={onFiltersChange}
                />
                <Detector onEnter={loadNextPage} disabled={loadDisabled} throttleDelay={500}>
                    {allLoaded ? (
                        endOfPage
                    ) : (
                        <RoundedButton
                            className={classNames([
                                styles.next,
                                {
                                    [styles.loading]: loading,
                                },
                            ])}
                            disabled={loading || loadDisabled}
                            onClick={loadNextPage}
                        >
                            {loading ? (
                                <FormattedMessage
                                    defaultMessage="Loading..."
                                    description="Button label"
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="View more projects"
                                    description="Button label"
                                />
                            )}
                        </RoundedButton>
                    )}
                </Detector>
            </div>
        </div>
    );
};

SectionPage.propTypes = propTypes;
SectionPage.defaultProps = defaultProps;

export default SectionPage;
